import React from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { LoadingAnimation } from '../../../components/ui/loading-animation';
import { PaginationControls } from '../../../components/ui/pagination';
import type { AdminUserListItem, OrganizationOption } from './types';

export type UserManagementListProps = {
  users: AdminUserListItem[];
  loading: boolean;
  error: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  selectedUserId: number | null;
  organizations: OrganizationOption[];
  selectedOrganization: string;
  onOrganizationChange: (value: string) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onSelectUser: (user: AdminUserListItem) => void;
  onCreateUser: () => void;
};

export const UserManagementList: React.FC<UserManagementListProps> = ({
  users,
  loading,
  error,
  search,
  onSearchChange,
  selectedUserId,
  organizations,
  selectedOrganization,
  onOrganizationChange,
  page,
  pageSize,
  total,
  onPageChange,
  onSelectUser,
  onCreateUser,
}) => {
  return (
    <Card disableAnimation maxWidth="none">
      <CardHeader>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle title="Select a user" />
          <Button variant="secondary" onClick={onCreateUser} size="auto">
            Create new user
          </Button>
        </div>
      </CardHeader>
      <CardContent variant="custom" className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1 min-w-[12.5rem]">
            <Label htmlFor="user-search">Search users</Label>
            <Input
              id="user-search"
              name="admin-user-directory-search"
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search by name or email"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className="w-full md:w-60">
            <Label htmlFor="org-filter">Filter by organization</Label>
            <Select value={selectedOrganization} onValueChange={onOrganizationChange}>
              <SelectTrigger id="org-filter">
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingAnimation />
          </div>
        ) : (
          <>
            {error ? (
              <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            ) : null}
            <div className="max-h-[520px] overflow-y-auto rounded-md border border-border/40">
              <table className="w-full text-sm">
                <thead className="bg-white/10 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Staff</th>
                    <th className="px-3 py-2 font-medium">Organization</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isSelected = selectedUserId === user.id;

                    return (
                      <tr
                        key={user.id}
                        className={`cursor-pointer border-t border-white/5 transition-colors ${
                          isSelected
                            ? 'border-l-4 border-l-primary bg-foundation-calm-blue-200'
                            : 'hover:bg-white/10'
                        }`}
                        aria-selected={isSelected}
                        onClick={() => onSelectUser(user)}
                      >
                        <td className="px-3 py-2 font-medium">{user.email}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {user.is_staff ? (
                            <span className="rounded bg-blue-500/20 px-1.5 py-0.5 text-blue-500">
                              Admin
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {user.organization_name ?? '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                        No users found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <PaginationControls page={page} pageSize={pageSize} totalCount={total} onPageChange={onPageChange} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
